import React, { useEffect } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Button, Col, FormText, Row } from 'reactstrap';
import { Translate, ValidatedField, ValidatedForm, isNumber, translate } from 'react-jhipster';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { useAppDispatch, useAppSelector } from 'app/config/store';

import { getEntities as getGlucoUsers } from 'app/entities/gluco-user/gluco-user.reducer';
import { getEntities as getWeights } from 'app/entities/weight/weight.reducer';
import { getEntities as getGlucoses } from 'app/entities/glucose/glucose.reducer';
import { getEntities as getAppointments } from 'app/entities/appointments/appointments.reducer';
import { getEntities as getVolunteerVisits } from 'app/entities/volunteer-visits/volunteer-visits.reducer';
import { getEntities as getBloodDonations } from 'app/entities/blood-donation/blood-donation.reducer';
import { BPMethod } from 'app/shared/model/enumerations/bp-method.model';
import { OximetryMethod } from 'app/shared/model/enumerations/oximetry-method.model';
import { InOutUnits } from 'app/shared/model/enumerations/in-out-units.model';
import { createEntity, getEntity, reset, updateEntity } from './vitals.reducer';

export const VitalsUpdate = () => {
  const dispatch = useAppDispatch();

  const navigate = useNavigate();

  const { id } = useParams<'id'>();
  const isNew = id === undefined;

  const glucoUsers = useAppSelector(state => state.glucoUser.entities);
  const weights = useAppSelector(state => state.weight.entities);
  const glucoses = useAppSelector(state => state.glucose.entities);
  const appointments = useAppSelector(state => state.appointments.entities);
  const volunteerVisits = useAppSelector(state => state.volunteerVisits.entities);
  const bloodDonations = useAppSelector(state => state.bloodDonation.entities);
  const vitalsEntity = useAppSelector(state => state.vitals.entity);
  const loading = useAppSelector(state => state.vitals.loading);
  const updating = useAppSelector(state => state.vitals.updating);
  const updateSuccess = useAppSelector(state => state.vitals.updateSuccess);
  const bPMethodValues = Object.keys(BPMethod);
  const oximetryMethodValues = Object.keys(OximetryMethod);
  const inOutUnitsValues = Object.keys(InOutUnits);

  const handleClose = () => {
    navigate(`/vitals${location.search}`);
  };

  useEffect(() => {
    if (isNew) {
      dispatch(reset());
    } else {
      dispatch(getEntity(id));
    }

    dispatch(getGlucoUsers({}));
    dispatch(getWeights({}));
    dispatch(getGlucoses({}));
    dispatch(getAppointments({}));
    dispatch(getVolunteerVisits({}));
    dispatch(getBloodDonations({}));
  }, []);

  useEffect(() => {
    if (updateSuccess) {
      handleClose();
    }
  }, [updateSuccess]);

  const saveEntity = values => {
    if (values.systolic !== undefined && typeof values.systolic !== 'number') {
      values.systolic = Number(values.systolic);
    }
    if (values.diastolic !== undefined && typeof values.diastolic !== 'number') {
      values.diastolic = Number(values.diastolic);
    }
    if (values.pulse !== undefined && typeof values.pulse !== 'number') {
      values.pulse = Number(values.pulse);
    }
    if (values.oximetry !== undefined && typeof values.oximetry !== 'number') {
      values.oximetry = Number(values.oximetry);
    }
    if (values.respiratoryRate !== undefined && typeof values.respiratoryRate !== 'number') {
      values.respiratoryRate = Number(values.respiratoryRate);
    }
    if (values.bodyTemp !== undefined && typeof values.bodyTemp !== 'number') {
      values.bodyTemp = Number(values.bodyTemp);
    }
    if (values.bodySurfaceArea !== undefined && typeof values.bodySurfaceArea !== 'number') {
      values.bodySurfaceArea = Number(values.bodySurfaceArea);
    }
    if (values.painScore !== undefined && typeof values.painScore !== 'number') {
      values.painScore = Number(values.painScore);
    }
    if (values.cursor !== undefined && typeof values.cursor !== 'number') {
      values.cursor = Number(values.cursor);
    }
    if (values.microSec !== undefined && typeof values.microSec !== 'number') {
      values.microSec = Number(values.microSec);
    }
    if (values.timestampentry !== undefined && typeof values.timestampentry !== 'number') {
      values.timestampentry = Number(values.timestampentry);
    }
    if (values.lastupdated !== undefined && typeof values.lastupdated !== 'number') {
      values.lastupdated = Number(values.lastupdated);
    }
    if (values.weight !== undefined && typeof values.weight !== 'number') {
      values.weight = Number(values.weight);
    }
    if (values.height !== undefined && typeof values.height !== 'number') {
      values.height = Number(values.height);
    }
    if (values.cvp !== undefined && typeof values.cvp !== 'number') {
      values.cvp = Number(values.cvp);
    }
    if (values.map !== undefined && typeof values.map !== 'number') {
      values.map = Number(values.map);
    }
    if (values.intake !== undefined && typeof values.intake !== 'number') {
      values.intake = Number(values.intake);
    }
    if (values.output !== undefined && typeof values.output !== 'number') {
      values.output = Number(values.output);
    }

    const entity = {
      ...vitalsEntity,
      ...values,
      glucoUser: glucoUsers.find(it => it.id.toString() === values.glucoUser?.toString()),
      weightEntry: weights.find(it => it.id.toString() === values.weightEntry?.toString()),
      glucoseEntry: glucoses.find(it => it.id.toString() === values.glucoseEntry?.toString()),
      appointment: appointments.find(it => it.id.toString() === values.appointment?.toString()),
      volunteerVisit: volunteerVisits.find(it => it.id.toString() === values.volunteerVisit?.toString()),
      bloodDonation: bloodDonations.find(it => it.id.toString() === values.bloodDonation?.toString()),
    };

    if (isNew) {
      dispatch(createEntity(entity));
    } else {
      dispatch(updateEntity(entity));
    }
  };

  const defaultValues = () =>
    isNew
      ? {}
      : {
          bpMethod: 'NIBP',
          oximetryMethod: 'SPO2',
          inOutUnits: 'mL',
          ...vitalsEntity,
          glucoUser: vitalsEntity?.glucoUser?.id,
          weightEntry: vitalsEntity?.weightEntry?.id,
          glucoseEntry: vitalsEntity?.glucoseEntry?.id,
          appointment: vitalsEntity?.appointment?.id,
          volunteerVisit: vitalsEntity?.volunteerVisit?.id,
          bloodDonation: vitalsEntity?.bloodDonation?.id,
        };

  return (
    <div>
      <Row className="justify-content-center">
        <Col md="8">
          <h2 id="tinyMedicRestApp.vitals.home.createOrEditLabel" data-cy="VitalsCreateUpdateHeading">
            <Translate contentKey="tinyMedicRestApp.vitals.home.createOrEditLabel">Create or edit a Vitals</Translate>
          </h2>
        </Col>
      </Row>
      <Row className="justify-content-center">
        <Col md="8">
          {loading ? (
            <p>Loading...</p>
          ) : (
            <ValidatedForm defaultValues={defaultValues()} onSubmit={saveEntity}>
              {!isNew ? (
                <ValidatedField
                  name="id"
                  required
                  readOnly
                  id="vitals-id"
                  label={translate('tinyMedicRestApp.vitals.id')}
                  validate={{ required: true }}
                />
              ) : null}
              <ValidatedField
                label={translate('tinyMedicRestApp.vitals.systolic')}
                id="vitals-systolic"
                name="systolic"
                data-cy="systolic"
                type="text"
                validate={{
                  required: { value: true, message: translate('entity.validation.required') },
                  validate: v => isNumber(v) || translate('entity.validation.number'),
                }}
              />
              <ValidatedField
                label={translate('tinyMedicRestApp.vitals.diastolic')}
                id="vitals-diastolic"
                name="diastolic"
                data-cy="diastolic"
                type="text"
                validate={{
                  required: { value: true, message: translate('entity.validation.required') },
                  validate: v => isNumber(v) || translate('entity.validation.number'),
                }}
              />
              <ValidatedField
                label={translate('tinyMedicRestApp.vitals.bpMethod')}
                id="vitals-bpMethod"
                name="bpMethod"
                data-cy="bpMethod"
                type="select"
              >
                {bPMethodValues.map(bPMethod => (
                  <option value={bPMethod} key={bPMethod}>
                    {translate(`tinyMedicRestApp.BPMethod.${bPMethod}`)}
                  </option>
                ))}
              </ValidatedField>
              <ValidatedField
                label={translate('tinyMedicRestApp.vitals.pulse')}
                id="vitals-pulse"
                name="pulse"
                data-cy="pulse"
                type="text"
              />
              <ValidatedField
                label={translate('tinyMedicRestApp.vitals.oximetryMethod')}
                id="vitals-oximetryMethod"
                name="oximetryMethod"
                data-cy="oximetryMethod"
                type="select"
              >
                {oximetryMethodValues.map(oximetryMethod => (
                  <option value={oximetryMethod} key={oximetryMethod}>
                    {translate(`tinyMedicRestApp.OximetryMethod.${oximetryMethod}`)}
                  </option>
                ))}
              </ValidatedField>
              <ValidatedField
                label={translate('tinyMedicRestApp.vitals.oximetry')}
                id="vitals-oximetry"
                name="oximetry"
                data-cy="oximetry"
                type="text"
              />
              <ValidatedField
                label={translate('tinyMedicRestApp.vitals.respiratoryRate')}
                id="vitals-respiratoryRate"
                name="respiratoryRate"
                data-cy="respiratoryRate"
                type="text"
              />
              <ValidatedField
                label={translate('tinyMedicRestApp.vitals.bodyTemp')}
                id="vitals-bodyTemp"
                name="bodyTemp"
                data-cy="bodyTemp"
                type="text"
              />
              <ValidatedField
                label={translate('tinyMedicRestApp.vitals.bodySurfaceArea')}
                id="vitals-bodySurfaceArea"
                name="bodySurfaceArea"
                data-cy="bodySurfaceArea"
                type="text"
              />
              <ValidatedField
                label={translate('tinyMedicRestApp.vitals.painScore')}
                id="vitals-painScore"
                name="painScore"
                data-cy="painScore"
                type="text"
              />
              <ValidatedField
                label={translate('tinyMedicRestApp.vitals.liverFunction')}
                id="vitals-liverFunction"
                name="liverFunction"
                data-cy="liverFunction"
                type="text"
              />
              <ValidatedField label={translate('tinyMedicRestApp.vitals.gfr')} id="vitals-gfr" name="gfr" data-cy="gfr" type="text" />
              <ValidatedField
                label={translate('tinyMedicRestApp.vitals.cursor')}
                id="vitals-cursor"
                name="cursor"
                data-cy="cursor"
                type="text"
                validate={{
                  required: { value: true, message: translate('entity.validation.required') },
                  validate: v => isNumber(v) || translate('entity.validation.number'),
                }}
              />
              <ValidatedField
                label={translate('tinyMedicRestApp.vitals.active')}
                id="vitals-active"
                name="active"
                data-cy="active"
                check
                type="checkbox"
              />
              <ValidatedField
                label={translate('tinyMedicRestApp.vitals.microSec')}
                id="vitals-microSec"
                name="microSec"
                data-cy="microSec"
                type="text"
                validate={{
                  required: { value: true, message: translate('entity.validation.required') },
                  validate: v => isNumber(v) || translate('entity.validation.number'),
                }}
              />
              <ValidatedField
                label={translate('tinyMedicRestApp.vitals.timestampentry')}
                id="vitals-timestampentry"
                name="timestampentry"
                data-cy="timestampentry"
                type="text"
                validate={{
                  required: { value: true, message: translate('entity.validation.required') },
                  validate: v => isNumber(v) || translate('entity.validation.number'),
                }}
              />
              <ValidatedField
                label={translate('tinyMedicRestApp.vitals.lastupdated')}
                id="vitals-lastupdated"
                name="lastupdated"
                data-cy="lastupdated"
                type="text"
                validate={{
                  required: { value: true, message: translate('entity.validation.required') },
                  validate: v => isNumber(v) || translate('entity.validation.number'),
                }}
              />
              <ValidatedField
                label={translate('tinyMedicRestApp.vitals.weight')}
                id="vitals-weight"
                name="weight"
                data-cy="weight"
                type="text"
              />
              <ValidatedField
                label={translate('tinyMedicRestApp.vitals.height')}
                id="vitals-height"
                name="height"
                data-cy="height"
                type="text"
              />
              <ValidatedField label={translate('tinyMedicRestApp.vitals.cvp')} id="vitals-cvp" name="cvp" data-cy="cvp" type="text" />
              <ValidatedField label={translate('tinyMedicRestApp.vitals.map')} id="vitals-map" name="map" data-cy="map" type="text" />
              <ValidatedField
                label={translate('tinyMedicRestApp.vitals.intake')}
                id="vitals-intake"
                name="intake"
                data-cy="intake"
                type="text"
              />
              <ValidatedField
                label={translate('tinyMedicRestApp.vitals.output')}
                id="vitals-output"
                name="output"
                data-cy="output"
                type="text"
              />
              <ValidatedField
                label={translate('tinyMedicRestApp.vitals.inOutUnits')}
                id="vitals-inOutUnits"
                name="inOutUnits"
                data-cy="inOutUnits"
                type="select"
              >
                {inOutUnitsValues.map(inOutUnits => (
                  <option value={inOutUnits} key={inOutUnits}>
                    {translate(`tinyMedicRestApp.InOutUnits.${inOutUnits}`)}
                  </option>
                ))}
              </ValidatedField>
              <ValidatedField
                id="vitals-glucoUser"
                name="glucoUser"
                data-cy="glucoUser"
                label={translate('tinyMedicRestApp.vitals.glucoUser')}
                type="select"
                required
              >
                <option value="" key="0" />
                {glucoUsers
                  ? glucoUsers.map(otherEntity => (
                      <option value={otherEntity.id} key={otherEntity.id}>
                        {otherEntity.id}
                      </option>
                    ))
                  : null}
              </ValidatedField>
              <FormText>
                <Translate contentKey="entity.validation.required">This field is required.</Translate>
              </FormText>
              <ValidatedField
                id="vitals-weightEntry"
                name="weightEntry"
                data-cy="weightEntry"
                label={translate('tinyMedicRestApp.vitals.weightEntry')}
                type="select"
              >
                <option value="" key="0" />
                {weights
                  ? weights.map(otherEntity => (
                      <option value={otherEntity.id} key={otherEntity.id}>
                        {otherEntity.id}
                      </option>
                    ))
                  : null}
              </ValidatedField>
              <ValidatedField
                id="vitals-glucoseEntry"
                name="glucoseEntry"
                data-cy="glucoseEntry"
                label={translate('tinyMedicRestApp.vitals.glucoseEntry')}
                type="select"
              >
                <option value="" key="0" />
                {glucoses
                  ? glucoses.map(otherEntity => (
                      <option value={otherEntity.id} key={otherEntity.id}>
                        {otherEntity.id}
                      </option>
                    ))
                  : null}
              </ValidatedField>
              <ValidatedField
                id="vitals-appointment"
                name="appointment"
                data-cy="appointment"
                label={translate('tinyMedicRestApp.vitals.appointment')}
                type="select"
              >
                <option value="" key="0" />
                {appointments
                  ? appointments.map(otherEntity => (
                      <option value={otherEntity.id} key={otherEntity.id}>
                        {otherEntity.id}
                      </option>
                    ))
                  : null}
              </ValidatedField>
              <ValidatedField
                id="vitals-volunteerVisit"
                name="volunteerVisit"
                data-cy="volunteerVisit"
                label={translate('tinyMedicRestApp.vitals.volunteerVisit')}
                type="select"
              >
                <option value="" key="0" />
                {volunteerVisits
                  ? volunteerVisits.map(otherEntity => (
                      <option value={otherEntity.id} key={otherEntity.id}>
                        {otherEntity.id}
                      </option>
                    ))
                  : null}
              </ValidatedField>
              <ValidatedField
                id="vitals-bloodDonation"
                name="bloodDonation"
                data-cy="bloodDonation"
                label={translate('tinyMedicRestApp.vitals.bloodDonation')}
                type="select"
              >
                <option value="" key="0" />
                {bloodDonations
                  ? bloodDonations.map(otherEntity => (
                      <option value={otherEntity.id} key={otherEntity.id}>
                        {otherEntity.id}
                      </option>
                    ))
                  : null}
              </ValidatedField>
              <Button tag={Link} id="cancel-save" data-cy="entityCreateCancelButton" to="/vitals" replace color="info">
                <FontAwesomeIcon icon="arrow-left" />
                &nbsp;
                <span className="d-none d-md-inline">
                  <Translate contentKey="entity.action.back">Back</Translate>
                </span>
              </Button>
              &nbsp;
              <Button color="primary" id="save-entity" data-cy="entityCreateSaveButton" type="submit" disabled={updating}>
                <FontAwesomeIcon icon="save" />
                &nbsp;
                <Translate contentKey="entity.action.save">Save</Translate>
              </Button>
            </ValidatedForm>
          )}
        </Col>
      </Row>
    </div>
  );
};

export default VitalsUpdate;
